import { useEffect, useRef } from "react"

/**
 * The live site's scroll animation: content fades up as it comes into view.
 *
 * Squarespace does this with `tweak-global-animations-animation-style-fade`
 * on every block; ours is the same effect on the section, which is what every
 * PLAYDATA page is built from, so one hook covers all of them.
 *
 * Two rules, because the failure mode here is a blank page rather than a
 * missing flourish:
 *
 * 1. The hidden state is opt-in from JavaScript, behind a class this hook
 *    puts on <html>. A reader whose script never runs sees the page as it is.
 * 2. Nothing waits on the observer to decide it is visible. Anything already
 *    in view is revealed from its own measurements, and a scroll listener
 *    backs the observer up, because `IntersectionObserver` does not fire at
 *    all while `document.hidden` is true — a background tab, a prerender, or
 *    an embedded viewport. Left to the observer alone, those readers get a
 *    column of nothing.
 * 3. A viewport that measures zero cannot say what is on screen, so nothing
 *    is hidden at all. Hidden documents report `innerHeight` of 0, and there
 *    "is it visible yet?" has no answer worth acting on.
 */
const READY_CLASS = "playdata-reveal-ready"
const REVEALED_CLASS = "is-revealed"

/** A little short of the fold, so a section is already in when it arrives. */
const VIEWPORT_MARGIN = 0.94

export function usePlaydataReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    const viewportIsMeasurable = window.innerHeight > 0
    if (prefersReducedMotion || !viewportIsMeasurable) {
      element.classList.add(REVEALED_CLASS)
      return
    }

    document.documentElement.classList.add(READY_CLASS)

    let observer: IntersectionObserver | undefined
    const stop = () => {
      observer?.disconnect()
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
    }
    const reveal = () => {
      element.classList.add(REVEALED_CLASS)
      stop()
    }
    const isInView = () => {
      const rect = element.getBoundingClientRect()
      return rect.top < window.innerHeight * VIEWPORT_MARGIN && rect.bottom > 0
    }
    function onScroll() {
      if (isInView()) reveal()
    }

    if (isInView()) {
      reveal()
      return
    }

    if (typeof IntersectionObserver === "function") {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) reveal()
        },
        { rootMargin: "0px 0px -6% 0px", threshold: 0.06 }
      )
      observer.observe(element)
    }
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll, { passive: true })

    return stop
  }, [])

  return ref
}
